import { Box, Paper, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import type { JobResponse } from '@taxassist/api-client';
import type { UserFriendlyStatus } from '../../utils/statusMapping';
import { ColumnHeader } from './ColumnHeader';
import { AdaptiveJobCard } from './AdaptiveJobCard';

interface KanbanColumnProps {
  status: UserFriendlyStatus;
  jobs: JobResponse[];
  isLoading: boolean;
  onCreateJob?: () => void;
  onUploadFiles?: (job: JobResponse) => void;
  onViewDetails?: (job: JobResponse) => void;
}

export function KanbanColumn({
  status,
  jobs,
  isLoading,
  onCreateJob,
  onUploadFiles,
  onViewDetails,
}: KanbanColumnProps) {
  // Determine if cards should be compact based on count
  const COMPACT_THRESHOLD = 8;
  const isCompactMode = jobs.length > COMPACT_THRESHOLD;

  return (
    <Paper
      elevation={1}
      sx={{
        minWidth: 280,
        width: '100%', // Take full grid column width
        display: 'flex',
        flexDirection: 'column',
        minHeight: 400, // Minimum height for empty states
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Column Header with Create Button */}
      <ColumnHeader 
        status={status} 
        jobCount={jobs.length} 
        isLoading={isLoading}
        onCreateJob={onCreateJob}
      />

      {/* Jobs List */}
      <Box
        sx={{
          flex: 1,
          p: 1,
          // Remove overflow constraints to let column grow
          display: 'flex',
          flexDirection: 'column',
          gap: isCompactMode ? 0.5 : 1,
        }}
      >
        {jobs.map((job) => (
          <AdaptiveJobCard 
            key={job.jobId} 
            job={job} 
            isCompact={isCompactMode}
            onUploadFiles={onUploadFiles} 
            onViewDetails={onViewDetails} 
          />
        ))}

        {/* Empty state or create button */}
        {jobs.length === 0 && !isLoading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
              color: 'text.secondary',
              textAlign: 'center',
              py: 3,
            }}
          >
            {onCreateJob ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={onCreateJob}
                  sx={{ mb: 2 }}
                >
                  Create Job
                </Button>
                <Box sx={{ fontSize: '0.875rem', opacity: 0.7 }}>
                  No jobs yet. Create your first job to get started.
                </Box>
              </>
            ) : (
              <Box sx={{ fontSize: '0.875rem', opacity: 0.7 }}>
                No jobs in this status
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Removed bottom Add Job button - now in header */}
    </Paper>
  );
}